import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { parseGeoPolygonString } from './workspaceFilterParams';

/**
 * Mapbox GL v2 rejects mapbox-gl-draw default line-dasharray expressions.
 * @see https://github.com/mapbox/mapbox-gl-draw/issues/1357
 */
export function createMapboxDrawStyles(): object[] {
  const styles = JSON.parse(JSON.stringify(MapboxDraw.lib.theme)) as Array<{
    paint?: Record<string, unknown>;
  }>;
  for (const layer of styles) {
    if (layer.paint && 'line-dasharray' in layer.paint) {
      layer.paint['line-dasharray'] = ['literal', [0.2, 2]];
    }
  }
  return styles;
}

export function boundsFromGeoPolygonRing(ring: number[][]): mapboxgl.LngLatBounds {
  const bounds = new mapboxgl.LngLatBounds();
  for (const [lng, lat] of ring) {
    bounds.extend([lng, lat]);
  }
  return bounds;
}

/** Remove all drawn features from MapboxDraw. */
export function clearGeoPolygonFromDraw(draw: MapboxDraw): void {
  draw.deleteAll();
  draw.changeMode('draw_polygon');
}

/**
 * MapboxDraw's built-in trash only deletes selected vertices in direct_select
 * mode, not the whole polygon. Append a trash button that clears the entire shape.
 */
export function appendClearPolygonButton(
  mapContainer: ParentNode,
  draw: MapboxDraw,
  onClear: () => void,
): void {
  const drawGroup = mapContainer
    .querySelector('.mapbox-gl-draw_polygon')
    ?.closest('.mapboxgl-ctrl-group');
  if (!drawGroup || drawGroup.querySelector('.mapbox-gl-draw_trash')) {
    return;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash';
  button.title = 'Delete polygon';
  button.setAttribute('aria-label', 'Delete polygon');
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearGeoPolygonFromDraw(draw);
    onClear();
  });

  drawGroup.appendChild(button);
}

/** Load an existing ring into MapboxDraw for editing; fit map to polygon bounds. */
export function loadGeoPolygonIntoDraw(
  draw: MapboxDraw,
  map: mapboxgl.Map,
  geoPolygonJson: string,
): string | null {
  const ring = parseGeoPolygonString(geoPolygonJson);
  if (!ring || ring.length < 3) {
    draw.changeMode('draw_polygon');
    return null;
  }

  try {
    clearGeoPolygonFromDraw(draw);
    const featureIds = draw.add({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [ring],
      },
    });
    map.fitBounds(boundsFromGeoPolygonRing(ring), {
      padding: 80,
      duration: 0,
      maxZoom: 15,
    });
    const ids = Array.isArray(featureIds) ? featureIds : [featureIds];
    const featureId = ids[0];
    if (featureId) {
      // simple_select so the whole feature is selected (built-in trash would work,
      // but we use a custom clear control). User can click the polygon to edit vertices.
      draw.changeMode('simple_select', { featureIds: ids });
    }
    return featureId ?? null;
  } catch (err) {
    console.warn('Failed to load existing geopolygon into editor:', err);
    draw.changeMode('draw_polygon');
    return null;
  }
}
